import type { PopoverProps as ChakraPopoverProps } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import FocusLock from 'react-focus-lock';

import {
  Popover,
  PopoverTrigger,
  Portal,
  PopoverBody,
  PopoverContent,
  Button,
  Text,
  PopoverButtonGroup,
  VStack,
  Box,
} from '@frontend/components';

type ChildProps = {
  variant?: string;
  children?: string | React.ReactNode;
  colorScheme?: string;
};

export interface PopconfirmProps
  extends React.PropsWithChildren<Omit<ChakraPopoverProps, 'children'>> {
  manuallyClose?: boolean;
  title?: string | React.ReactNode;
  label?: string | React.ReactNode;
  contents?: string | React.ReactNode;
  onConfirm?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCancel?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  cancelProps?: ChildProps;
  confirmProps?: ChildProps;
}

export const Popconfirm = ({
  children,
  title,
  label,
  contents,
  isOpen,
  onConfirm,
  onCancel,
  manuallyClose = false,
  isLazy = true,
  cancelProps = undefined,
  confirmProps,
  ...props
}: PopconfirmProps) => {
  const confirmRef = useRef(null);

  return (
    <Popover
      initialFocusRef={confirmRef}
      isOpen={isOpen}
      isLazy={isLazy}
      {...props}
    >
      {({ onClose }) => (
        <>
          <PopoverTrigger>{children}</PopoverTrigger>
          <Portal>
            <PopoverContent>
              <FocusLock persistentFocus>
                <PopoverBody as={VStack} spacing={0.5} align="stretch">
                  {typeof title === 'string' ? (
                    <Text fontWeight="heavy" as="strong" display="block">
                      {title}
                    </Text>
                  ) : (
                    title
                  )}
                  <Box>
                    {typeof label === 'string' ? <Text>{label}</Text> : label}
                  </Box>
                  {contents}
                  <Box>
                    <PopoverButtonGroup w="full" justifyContent="end" size="sm">
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          onCancel?.(e);
                          onClose();
                        }}
                        {...cancelProps}
                      >
                        {cancelProps?.children || 'Cancel'}
                      </Button>
                      <Button
                        ref={confirmRef}
                        variant="primary"
                        onClick={(e) => {
                          onConfirm?.(e);
                          !manuallyClose && onClose();
                        }}
                        {...confirmProps}
                      >
                        {confirmProps?.children || 'Confirm'}
                      </Button>
                    </PopoverButtonGroup>
                  </Box>
                </PopoverBody>
              </FocusLock>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};

Popconfirm.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isOpen: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  //colorScheme: 'destructive',
  placement: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end',
    'start-start',
    'start-end',
    'end-start',
    'end-end',
    'auto',
  ]),
};

Popconfirm.defaultProps = {
  flip: true,
  closeOnEsc: true,
  closeOnBlur: true,
};
